/**
 * Super Admin - Companies Management con subida de logo
 */

import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';

export function SuperAdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLogo, setEditingLogo] = useState(null); // companyId que está editando logo
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileInputRef = useRef();

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'companies'));
      setCompanies(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e, companyId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setUploadMsg('Solo PNG, JPG, SVG o WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadMsg('Máximo 2 MB');
      return;
    }

    setUploading(true);
    setUploadMsg('Subiendo...');
    try {
      const storageRef = ref(storage, `companies/${companyId}/branding/logo`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'companies', companyId), {
        'branding.logo': url
      });

      setCompanies(prev => prev.map(c =>
        c.id === companyId
          ? { ...c, branding: { ...(c.branding || {}), logo: url } }
          : c
      ));
      setUploadMsg('✅ Logo actualizado');
      setTimeout(() => { setEditingLogo(null); setUploadMsg(''); }, 1500);
    } catch (err) {
      console.error(err);
      setUploadMsg('❌ Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const filtered = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando empresas...</div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="title" style={{ fontSize: 24, marginBottom: 4 }}>Empresas</h1>
          <p className="meta">Gestiona todas las empresas del sistema</p>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Buscar empresas..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ maxWidth: 400 }}
      />

      {/* List */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#64748b' }}>
            {searchTerm ? 'No se encontraron empresas' : 'No hay empresas registradas'}
          </div>
        ) : (
          filtered.map((company, idx) => (
            <div key={company.id} style={{
              padding: '16px 20px',
              borderBottom: idx < filtered.length - 1 ? '1px solid #f1f5f9' : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 16
            }}>
              {/* Logo */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {company.branding?.logo ? (
                  <img
                    src={company.branding.logo}
                    alt={company.name}
                    style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'contain', border: '1px solid #e2e8f0', background: '#fff' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: 44, height: 44, borderRadius: 8,
                    background: company.branding?.primaryColor || '#273067',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 14, color: '#fff'
                  }}>
                    {(company.name || 'E').slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{company.name}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', gap: 12 }}>
                  <span>Tipo: {getCompanyTypeDisplay(company.type)}</span>
                  <span>Plan: {company.subscription?.plan || 'basic'}</span>
                  <span style={{ color: company.active ? '#166534' : '#94a3b8' }}>
                    {company.active ? '● Activa' : '○ Inactiva'}
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                {editingLogo === company.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      style={{ display: 'none' }}
                      onChange={(e) => handleLogoUpload(e, company.id)}
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn"
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        style={{ fontSize: 12, padding: '6px 14px' }}
                      >
                        {uploading ? 'Subiendo...' : '📁 Elegir imagen'}
                      </button>
                      <button
                        className="copy"
                        onClick={() => { setEditingLogo(null); setUploadMsg(''); }}
                        style={{ fontSize: 12, padding: '6px 10px' }}
                      >
                        Cancelar
                      </button>
                    </div>
                    {uploadMsg && (
                      <span style={{ fontSize: 11, color: uploadMsg.startsWith('✅') ? '#166534' : '#991b1b' }}>
                        {uploadMsg}
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    className="copy"
                    onClick={() => setEditingLogo(company.id)}
                    style={{ fontSize: 12, padding: '6px 14px' }}
                  >
                    🖼️ Logo
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function getCompanyTypeDisplay(type) {
  const types = { albaran: 'Albarán', parte_trabajo: 'Parte de Trabajo', custom: 'Personalizado' };
  return types[type] || type;
}

export default SuperAdminCompanies;
